import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  input,
  model,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AbbButtonComponent } from '@components/abb-button/abb-button.component';
import { AbbIconComponent } from '@components/abb-icon/abb-icon.component';
import { AbbSidePanelComponent } from '@components/abb-side-panel/abb-side-panel.component';
import {
  SIDE_PANEL_DATA,
  SidePanelData,
} from '@components/abb-side-panel/side-panel';
import { BUTTONSIZE } from '@constants/buttons';
import {
  DashboardSidePanelData,
  WidgetDisplayOptionMapping,
  WidgetType,
  WidgetTypeCategory,
  WidgetTypeCategoryDef,
  WidgetTypeDef,
} from '@constants/widgets';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { firstValueFrom, Subscription } from 'rxjs';
import { DashboardSidePanelFormComponent } from '../dashboard-side-panel-form/dashboard-side-panel-form.component';
import { LOADING } from '@utils/loader';
import { MatExpansionModule } from '@angular/material/expansion';
import { createWidgetMutation, updateWidgetMutation } from '@graphql/dashboard';
import { ToastService } from '@services/ui/toast.service';
import { Apollo } from 'apollo-angular';
import {
  CreateWidgetMutation,
  CreateWidgetMutationVariables,
  UpdateWidgetMutation,
  UpdateWidgetMutationVariables,
} from 'gql/graphql';
import { TranslateService } from '@ngx-translate/core';
import { defer, ignoreErrorAsync } from '@utils/runtime';
import { OutputSignalContent } from '@utils/angular-ext';
import { t } from '@utils/strings';

@Component({
  selector: 'abb-dashboard-side-panel',
  standalone: true,
  imports: [
    AbbSidePanelComponent,
    CommonModule,
    TPipe,
    AbbIconComponent,
    AbbButtonComponent,
    FormsModule,
    DashboardSidePanelFormComponent,
    MatExpansionModule,
  ],
  // TODO: improve min-w mechanism
  template: `
    <abb-side-panel
      [hasBackdrop]="hasBackdrop()"
      contentWrapperClass="min-w-[787px]"
      #panelRef
      [backdropClickClosable]="!loading()"
      [closable]="!loading()"
    >
      <div header>
        @if (selectedWidgetType()) {
        <abb-button
          variant="clean"
          className="font-normal text-xs gap-2 !justify-start"
          (onclick)="resetWidgetTypeSelection()"
          [icon]="'mat/arrow_back'"
          class="w-60"
        >
          <span class="block w-full capitalize">{{
            'Widget selection' | t
          }}</span>
        </abb-button>
        }
        <h2 class="font-semibold text-2xl py-3 capitalize">
          {{
            selectedWidgetType()
              ? ('{type} widget' | t : { type: selectedWidgetType()?.name | t })
              : ('New widget' | t)
          }}
        </h2>
      </div>
      @if (selectedWidgetType(); as widgetType) {

      <abb-dashboard-side-panel-form
        [widgetType]="widgetType.id"
        (cancel)="panelRef.handleClose()"
        [widgetData]="data.widget"
        [mode]="data.mode"
        (abbSubmit)="saveWidget($event)"
      ></abb-dashboard-side-panel-form>
      } @else {

      <p class="capitalize font-medium my-4 text-abb-font/85">
        {{ 'Widget type' | t }}
      </p>
      <mat-accordion multi class="[&_::after]:border-abb-font/85">
        @for (widgetTypeCategory of widgetTypeCategories; track
        widgetTypeCategory.id) {
        <mat-expansion-panel>
          <mat-expansion-panel-header
            ><mat-panel-title class="capitalize !text-abb-font/85">
              {{ widgetTypeCategory.name | t }}
            </mat-panel-title></mat-expansion-panel-header
          >
          <div
            class="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-x-8 lg:gap-y-6"
          >
            @for (widgetType of widgetTypeCategory.widgetTypes; track
            widgetType.id) {
            <button
              class="shadow-lg h-32 rounded px-4 py-6 sm:max-w-56 md:max-w-64 lg:max-w-72 flex flex-col text-left transition-shadow active:shadow-inner focus:outline focus:outline-2 focus:shadow-sm hover:shadow-sm"
              (click)="selectType(widgetType)"
            >
              <abb-icon size="large" [name]="widgetType.icon"></abb-icon>
              <span class="font-semibold text-abb-font/85">{{
                widgetType.name | t
              }}</span>
              <span class="font-light text-xs text-abb-font/60">{{
                widgetType.description | t
              }}</span>
            </button>
            }
          </div></mat-expansion-panel
        >
        }</mat-accordion
      >}
      <div footer class="w-full flex flex-row gap-8 justify-end p-8">
        <abb-button
          [disabled]="loading()"
          variant="clean"
          (onclick)="onCancel()"
          >{{ 'cancel' | t }}</abb-button
        >
        <abb-button
          class="w-32"
          type="submit"
          form="dashboard-side-pane-form"
          [loading]="loading()"
          [disabled]="!selectedWidgetType() || loading()"
          >{{
            data.mode === 'edit' ? ('Update' | t) : ('Create' | t)
          }}</abb-button
        >
      </div>
    </abb-side-panel>
  `,
  styles: `
  @keyframes fade-in {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  .animate-fade-in {
    animation: fade-in 0.3s ease-out;
  }

  `,
  providers: [
    { provide: TRANSLATION_PREFIX, useValue: 'dashboards.side_panel' },
  ],
})
export class DashboardSidePanelComponent
  implements OnDestroy, SidePanelData<DashboardSidePanelData>
{
  onCancel() {
    this.panelRef()?.dispose();
  }
  WidgetTypeCategory = WidgetTypeCategory;
  readonly BUTTONSIZE = BUTTONSIZE;
  hasBackdrop = input<boolean>(true);
  panelRef = viewChild<AbbSidePanelComponent>('panelRef');
  private subscriptions: Subscription[] = [];
  data = inject<DashboardSidePanelData>(SIDE_PANEL_DATA);
  selectedWidgetType = signal<WidgetTypeDef | null>(null);

  // chart form
  metricType = model();

  widgetTypeCategories: WidgetTypeCategoryDef[] = [
    {
      id: WidgetTypeCategory.ALARMS_MONITORING,
      name: 'Alarms monitoring',
      widgetTypes: [
        {
          description:
            'Data presentation of number of alarms to be acknowledged',
          icon: 'mat-outlined/warning',
          id: WidgetType.N_OF_ALARMS,
          name: 'N. of alarms',
        },
        {
          description: 'Data presentation of alarms triggered',
          icon: 'mat-outlined/content_paste',
          id: WidgetType.ALARM_LOG,
          name: 'Alarm log',
        },
      ],
    },
    {
      id: WidgetTypeCategory.ASSET_MONITORING,
      name: 'Asset monitoring',
      widgetTypes: [
        {
          description: 'Visualization of the current devices status',
          icon: 'mat-outlined/wifi_tethering',
          id: WidgetType.DEVICE_STATUS,
          name: 'Device status',
        },
        {
          description: 'Data presentation of online events',
          icon: 'mat/calendar_month',
          id: WidgetType.ONLINE_EVENTS,
          name: 'Online events',
        },
      ],
    },
    {
      id: WidgetTypeCategory.COST_MANAGEMENT,
      name: 'Cost management',
      widgetTypes: [
        {
          icon: 'mat-outlined/point_of_sale',
          name: 'Cost benchmark',
          id: WidgetType.COST_BENCHMARK,
          description:
            'Cost comparison data between properties, groups or devices',
        },
        {
          icon: 'mat-outlined/energy_savings_leaf',
          name: 'Energy cost breakdown',
          id: WidgetType.ENERGY_COST_BREAKDOWN,
          description: 'Cost details per property or group',
        },
        {
          icon: 'mat-outlined/euro',
          name: 'Contracts & Tariffs',
          id: WidgetType.CONTRACTS_AND_TARIFFS,
          description: 'Data presentation of contracts & tariffs values',
        },
      ],
    },
    {
      id: WidgetTypeCategory.DATA_ANALYSIS,
      name: 'Data analysis',
      widgetTypes: [
        {
          icon: 'mat-outlined/table_chart',
          name: 'Actual vs Target Values',
          id: WidgetType.ACTUAL_VS_TARGET_VALUES,
          description: 'Comparison of real values vs targeted ones',
        },
        {
          icon: 'mat-outlined/flag',
          name: 'KPIs Calculation',
          id: WidgetType.KPI_CALCULATION,
          description: 'KPIs calculation based on existing parameters',
        },
      ],
    },
    {
      id: WidgetTypeCategory.ENERGY_MONITORING,
      name: 'Energy monitoring',
      widgetTypes: [
        {
          icon: 'mat-outlined/show_chart',
          name: 'Electrical values',
          id: WidgetType.ELECTRICAL_VALUES,
          description: 'Data presentation of general electrical values',
        },
        {
          icon: 'abb/bar-chart',
          name: 'Energy values',
          id: WidgetType.ENERGY_VALUES,
          description:
            'Data presentation of values from category "Energy values"',
        },
        {
          icon: 'abb/trend-2',
          name: 'Power quality values',
          id: WidgetType.POWER_QUALITY,
          description:
            'Data presentation of values from category "Power Quality Values"',
        },
        {
          icon: 'abb/trend-scatter',
          name: 'Custom values',
          id: WidgetType.CUSTOM_VALUES,
          description: 'Data presentation of values from other categories',
        },
        {
          icon: 'mat-outlined/speed',
          name: 'Max / Min values',
          id: WidgetType.MAX_MIN_VALUES,
          description:
            'Data visualization of Max, Min and Avg values for established device conditions',
        },
        {
          icon: 'abb/bar-chart',
          name: 'Energy consumption',
          id: WidgetType.ENERGY_CONSUMPTION,
          description:
            'Data presentation of "Energy values" between different types of devices',
        },
      ],
    },
    {
      id: WidgetTypeCategory.NON_ELECTRICAL_PARAMETERS,
      name: 'Non-electrical parameters',
      widgetTypes: [
        {
          icon: 'mat-outlined/bubble_chart',
          description: 'Data presentation of Water / Gas data',
          id: WidgetType.WATER_GAS,
          name: 'Water / Gas',
        },
      ],
    },
    {
      id: WidgetTypeCategory.PROPERTIES,
      name: 'Properties',
      widgetTypes: [
        {
          icon: 'mat-outlined/text_fields',
          description: 'Widget for customizable text options',
          id: WidgetType.TEXT,
          name: 'Text',
        },
        {
          icon: 'mat/location_on',
          description: 'Geolocation Visualization',
          id: WidgetType.LOCATION,
          name: 'Location',
        },
      ],
    },
    {
      id: WidgetTypeCategory.SUSTAINABILITY,
      name: 'Sustainability',
      widgetTypes: [
        {
          icon: 'mat-outlined/co2',
          description: 'Measure the impact of your overall consumption',
          id: WidgetType.CO2_EMISSIONS,
          name: 'CO2 Emissions',
        },
      ],
    },
  ];
  loading = inject(LOADING);
  constructor(
    private readonly apollo: Apollo,
    private readonly toast: ToastService,
    private readonly translate: TranslateService
  ) {
    if (this.data.widget) {
      const type = this.widgetTypeCategories
        .flatMap((c) => c.widgetTypes)
        .find((t) => t.id === this.data.widget?.type);
      if (type) {
        this.selectedWidgetType.set(type);
      }
      // TODO: permettere il cambio tipo?
    }
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
  selectType(widgetType: WidgetTypeDef) {
    this.selectedWidgetType.set(widgetType);
  }
  resetWidgetTypeSelection() {
    this.selectedWidgetType.set(null);
  }

  private getFirstAvailableSpot() {
    const rows = (this.data.widgets ?? []).reduce(
      (maxRow, w) => Math.max(w.row + w.rowSpan, maxRow),
      0
    );
    return { col: 1, row: rows + 1 };
  }

  private prefix = inject(TRANSLATION_PREFIX);

  async saveWidget({
    sharedVariables,
    display,
  }: OutputSignalContent<DashboardSidePanelFormComponent['abbSubmit']>) {
    try {
      this.loading.set(true);

      if (this.data.submitOverride) {
        await this.data.submitOverride({
          sharedVariables,
          display,
        });
        defer(() => this.panelRef()?.handleClose());
      } else {
        if (this.data.mode !== 'edit' || !this.data.widget) {
          const position = this.getFirstAvailableSpot();
          try {
            await firstValueFrom(
              this.apollo.mutate<
                CreateWidgetMutation,
                CreateWidgetMutationVariables
              >({
                mutation: createWidgetMutation,
                variables: {
                  ...sharedVariables,
                  dashboardId: this.data.dashboardId,
                  col: position.col,
                  row: position.row,
                  rowSpan: WidgetDisplayOptionMapping[display].rowSpan,
                  colSpan: WidgetDisplayOptionMapping[display].colSpan,
                  isDuplication: this.data.mode === 'clone',
                },
              })
            );
            this.toast.notifySuccess(
              t(this.prefix,
                this.data.mode === 'clone'
                  ? `Widget duplicated successfully`
                  : `Widget created successfully`,
                this.translate
              )
            );
            await ignoreErrorAsync(() =>
              this.apollo.client.refetchQueries({
                include: ['GetDashboards'],
              })
            );
            defer(() => this.panelRef()?.handleClose());
          } catch (err) {
            console.error(err);
            this.toast.notifyError(
              t(
                this.prefix,
                `An error occurred while creating the widget`,
                this.translate
              )
            );
          }
        } else {
          try {
            await firstValueFrom(
              this.apollo.mutate<
                UpdateWidgetMutation,
                UpdateWidgetMutationVariables
              >({
                mutation: updateWidgetMutation,
                variables: {
                  id: this.data.widget.id,
                  dashboardId: this.data.dashboardId,
                  ...sharedVariables,
                },
              })
            );
            this.toast.notifySuccess(
              t(this.prefix, `Widget updated successfully`, this.translate)
            );
            await ignoreErrorAsync(() =>
              this.apollo.client.refetchQueries({
                include: ['GetDashboards', 'GetWidgetData'],
              })
            );
            defer(() => this.panelRef()?.handleClose());
          } catch (err) {
            console.error(err);
            this.toast.notifyError(
              t(
                this.prefix,
                `An error occurred while updating the widget`,
                this.translate
              )
            );
          }
        }
      }
    } finally {
      this.loading.set(false);
    }
  }
}
